
import React from 'react';
import classnames from 'classnames';

import { Grid, ButtonBase, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { Icon, Translate } from '@hydra/common/components';
import { useI18n } from '@hydra/common/customHooks';

const useStyles = makeStyles(theme => ({
  root: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(4),
    [theme.breakpoints.up('sm')]: {
      marginTop: theme.spacing(4)
    }
  },
  header: {
    marginTop: theme.spacing(0),
    display: 'flex',
    justifyContent: 'space-between',
    [theme.breakpoints.up('sm')]: {
      marginTop: theme.spacing(2)
    }
  },
  gutters: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      marginTop: theme.spacing(4)
    }
  },
  hiddenXs: {
    display: 'none',
    [theme.breakpoints.up('sm')]: {
      display: 'flex'
    }
  },
  icon: { marginRight: theme.spacing(2) },
  entry: {
    background: theme.palette.background.paper,
    padding: `${theme.spacing(4)}px ${theme.spacing(3)}px`,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius * 2.5,
    display: 'flex',
    alignItems: 'flex-start',
    textAlign: 'left',
    width: '100%',
    transition: theme.transitions.create(
      ['color'],
      { duration: theme.transitions.duration.short }
    ),
    '&:hover, &:focus, &:active': { textDecoration: 'none !important' },
    '&:hover .arrow': { opacity: 1 }
  },
  entryTextWrapper: { width: '100%' },
  entryHeader: {
    display: 'flex',
    position: 'relative',
    marginBottom: theme.spacing(1)
  },
  entryTitle: { fontSize: theme.typography.fontSizeRoot * 1.375 },
  homeText: { fontSize: theme.typography.fontSizeRoot * 1.75 },
  bold: { fontWeight: theme.typography.fontWeightSemibold },
  arrow: {
    position: 'absolute',
    top: `-${theme.spacing(0.3)}px`,
    right: `-${theme.spacing(1)}px`,
    opacity: 0,
    transition: theme.transitions.create(
      ['opacity'],
      { duration: theme.transitions.duration.short }
    )
  }
}));

type EntryProps = {
  title: string
  body: string
  url: string
  icon: React.ReactNode
  trackingName: string
}

export default function CardEntries () {
  const { locale } = useI18n();
  const classes = useStyles();

  const entries:Array<EntryProps> = [
    {
      title: 'home_card_optimize_header',
      body: 'home_card_optimize_description',
      url: `/${locale}/optimize`,
      icon: <Icon type='optimize-app' className={classes.icon} color='primary' />,
      trackingName: 'appify-home-optimize-card'
    },
    {
      title: 'home_card_sell_header',
      body: 'home_card_sell_description',
      url: `/${locale}/audit`,
      icon: <Icon type='audit' className={classes.icon} color='primary' />,
      trackingName: 'appify-home-sell-card'
    },
    {
      title: 'home_card_pdf_header',
      body: 'home_card_pdf_description',
      url: `/${locale}/pdf-editor`,
      icon: <Icon type='pdf-templates' className={classes.icon} color='primary' />,
      trackingName: 'appify-home-pdf-card'
    }
  ];

  return (
    <>
      {
        entries.map(entry => (
          <Grid key={entry.url} item container lg={4} sm={6} xs={12}>
            <ButtonBase href={entry.url} className={classes.entry} data-value={entry.trackingName}>
              <div className={classes.entryTextWrapper}>
                <div className={classes.entryHeader}>
                  {entry.icon}
                  <Typography
                    variant='h4'
                    component='h3'
                    color='textPrimary'
                    className={classnames(classes.bold, classes.entryTitle)}
                    gutterBottom
                  >
                    <Translate content={entry.title} />
                  </Typography>
                  <Icon
                    type='chevron-right'
                    size='lg'
                    className={`${classes.arrow} arrow`}
                    color='primary'
                  />
                </div>
                <Typography color='textSecondary'>
                  <Translate content={entry.body} />
                </Typography>
              </div>
            </ButtonBase>
          </Grid>
        ))
      }
    </>
  );
}
